import { useCallback, useEffect, useState } from "react";
import {
  Title2,
  Body1,
  Badge,
  Spinner,
  Button,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Field,
  Textarea,
  Toaster,
  useToastController,
  useId,
  Toast,
  ToastTitle,
  ToastBody,
  makeStyles,
  tokens
} from "@fluentui/react-components";
import { fetchWithRetry } from "../lib/fetchWithRetry";

interface PendingRequest {
  id: string;
  requestNumber: number;
  status: "pending";
  leaveTypeId: string;
  leaveTypeName: string;
  requesterId: string;
  requesterName: string;
  requesterEmail: string;
  requesterDepartmentName: string | null;
  startDate: string;
  endDate: string;
  workDays: number;
  halfDay: boolean;
  amPm: "AM" | "PM" | null;
  notes: string | null;
  submittedAt: string | null;
  createdAt: string;
  viaSubstitution: { managerId: string; managerName: string } | null;
}

interface ApprovalsResponse {
  scope: "manager" | "admin";
  requests: PendingRequest[];
}

const useStyles = makeStyles({
  headerRow: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalL,
    marginBottom: tokens.spacingVerticalS
  },
  empty: {
    padding: tokens.spacingVerticalXL,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium
  },
  error: { color: tokens.colorPaletteRedForeground1 },
  notesCell: {
    maxWidth: "260px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  actionsCell: {
    whiteSpace: "nowrap",
    display: "flex",
    gap: tokens.spacingHorizontalS
  },
  denyBody: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM
  }
});

// API dates are YYYY-MM-DD; parsed as midnight UTC, they render as the prior
// day in west-of-UTC timezones. Pin the formatter to UTC.
function formatDateRange(startIso: string, endIso: string): string {
  const fmt = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
  if (startIso === endIso) return fmt.format(new Date(startIso));
  const startD = new Date(startIso);
  const endD = new Date(endIso);
  if (startD.getUTCFullYear() === endD.getUTCFullYear()) {
    const startShort = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" }).format(startD);
    return `${startShort} – ${fmt.format(endD)}`;
  }
  return `${fmt.format(startD)} – ${fmt.format(endD)}`;
}

function formatDays(r: PendingRequest): string {
  if (r.halfDay) return `½ day${r.amPm ? ` (${r.amPm})` : ""}`;
  return r.workDays === 1 ? "1 day" : `${r.workDays} days`;
}

function formatSubmittedAge(iso: string | null): string {
  if (!iso) return "—";
  const submitted = new Date(iso).getTime();
  if (!Number.isFinite(submitted)) return "—";
  const diffMs = Date.now() - submitted;
  const day = 24 * 60 * 60 * 1000;
  const hr = 60 * 60 * 1000;
  if (diffMs < hr) {
    const mins = Math.max(1, Math.round(diffMs / 60_000));
    return `${mins}m ago`;
  }
  if (diffMs < day) {
    const hrs = Math.round(diffMs / hr);
    return `${hrs}h ago`;
  }
  const days = Math.round(diffMs / day);
  return `${days}d ago`;
}

interface DenyDialogProps {
  open: boolean;
  request: PendingRequest | null;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
}

function DenyDialog({ open, request, onClose, onSubmit }: DenyDialogProps) {
  const styles = useStyles();
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason("");
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  if (!request) return null;
  const canSubmit = reason.trim().length > 0 && !submitting;

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(reason.trim());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(_, d) => !d.open && onClose()} modalType="modal">
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Deny request #{request.requestNumber}?</DialogTitle>
          <DialogContent>
            <div className={styles.denyBody}>
              <Body1>
                {request.requesterName} · {request.leaveTypeName} · {formatDateRange(request.startDate, request.endDate)} · {formatDays(request)}
              </Body1>
              <Field label="Reason for denial" required>
                <Textarea
                  value={reason}
                  onChange={(_, d) => setReason(d.value)}
                  rows={3}
                  placeholder="Briefly explain why this request is being denied. The requester will see this."
                />
              </Field>
              {error && <Body1 className={styles.error}>{error}</Body1>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button appearance="primary" onClick={handleSubmit} disabled={!canSubmit}>
              {submitting ? "Denying…" : "Deny request"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

export default function Approvals() {
  const styles = useStyles();
  const toasterId = useId("approvals-toaster");
  const { dispatchToast } = useToastController(toasterId);

  const [data, setData] = useState<ApprovalsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warming, setWarming] = useState(false);
  const [actingId, setActingId] = useState<string | null>(null);
  const [denyTarget, setDenyTarget] = useState<PendingRequest | null>(null);

  const load = useCallback(() => {
    setError(null);
    return fetchWithRetry("/api/approvals", undefined, { onWarming: () => setWarming(true) })
      .then(async (r) => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.error ?? `HTTP ${r.status}`);
        }
        return (await r.json()) as ApprovalsResponse;
      })
      .then((d) => { setData(d); setWarming(false); })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  useEffect(() => {
    setData(null);
    setWarming(false);
    load();
  }, [load]);

  async function approve(r: PendingRequest) {
    setActingId(r.id);
    try {
      const res = await fetch(`/api/leave-requests/${encodeURIComponent(r.id)}/approve`, { method: "POST" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      dispatchToast(
        <Toast><ToastTitle>Approved</ToastTitle><ToastBody>Request #{r.requestNumber} approved.</ToastBody></Toast>,
        { intent: "success" }
      );
      await load();
    } catch (e) {
      dispatchToast(
        <Toast><ToastTitle>Couldn't approve</ToastTitle><ToastBody>{e instanceof Error ? e.message : String(e)}</ToastBody></Toast>,
        { intent: "error" }
      );
    } finally {
      setActingId(null);
    }
  }

  async function deny(r: PendingRequest, reason: string) {
    setActingId(r.id);
    try {
      const res = await fetch(`/api/leave-requests/${encodeURIComponent(r.id)}/deny`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason })
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      dispatchToast(
        <Toast><ToastTitle>Denied</ToastTitle><ToastBody>Request #{r.requestNumber} denied.</ToastBody></Toast>,
        { intent: "success" }
      );
      setDenyTarget(null);
      await load();
    } catch (e) {
      // Rethrow so the dialog can surface inline + leave open for retry.
      throw e;
    } finally {
      setActingId(null);
    }
  }

  if (error) {
    return (
      <div>
        <Toaster toasterId={toasterId} />
        <Title2 as="h2" block>Approvals</Title2>
        <Body1 as="p" className={styles.error}>Error loading approvals: {error}</Body1>
      </div>
    );
  }

  if (!data) {
    return (
      <div>
        <Toaster toasterId={toasterId} />
        <Title2 as="h2" block>Approvals</Title2>
        <Spinner
          size="tiny"
          label={warming ? "Waking up the database… 30–60s the first time after hours." : "Loading…"}
        />
      </div>
    );
  }

  const scopeLabel = data.scope === "admin" ? "all pending requests" : "your direct reports' pending requests";

  return (
    <div>
      <Toaster toasterId={toasterId} />
      <div className={styles.headerRow}>
        <div>
          <Title2 as="h2" block>Approvals</Title2>
          <Body1 as="p">
            Showing {scopeLabel}. {data.requests.length} item{data.requests.length === 1 ? "" : "s"}.
          </Body1>
        </div>
      </div>

      {data.requests.length === 0 ? (
        <div className={styles.empty}>
          <Body1>Nothing pending right now.</Body1>
        </div>
      ) : (
        <Table aria-label="Pending leave requests">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>#</TableHeaderCell>
              <TableHeaderCell>Requester</TableHeaderCell>
              <TableHeaderCell>Type</TableHeaderCell>
              <TableHeaderCell>Dates</TableHeaderCell>
              <TableHeaderCell>Days</TableHeaderCell>
              <TableHeaderCell>Submitted</TableHeaderCell>
              <TableHeaderCell>Notes</TableHeaderCell>
              <TableHeaderCell aria-label="Actions" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.requests.map((r) => {
              const acting = actingId === r.id;
              const detail = r.notes ?? "";
              return (
                <TableRow key={r.id}>
                  <TableCell>{r.requestNumber}</TableCell>
                  <TableCell>
                    <div style={{ display: "flex", alignItems: "center", gap: tokens.spacingHorizontalXS, flexWrap: "wrap" }}>
                      <span>{r.requesterName}</span>
                      {r.viaSubstitution && (
                        <Badge
                          appearance="tint"
                          color="informative"
                          size="small"
                          title={`You're acting as substitute for ${r.viaSubstitution.managerName}`}
                        >
                          for {r.viaSubstitution.managerName}
                        </Badge>
                      )}
                    </div>
                    {r.requesterDepartmentName && (
                      <Body1 style={{ color: tokens.colorNeutralForeground3 }}>
                        {r.requesterDepartmentName}
                      </Body1>
                    )}
                  </TableCell>
                  <TableCell>{r.leaveTypeName}</TableCell>
                  <TableCell>{formatDateRange(r.startDate, r.endDate)}</TableCell>
                  <TableCell>{formatDays(r)}</TableCell>
                  <TableCell>{formatSubmittedAge(r.submittedAt)}</TableCell>
                  <TableCell className={styles.notesCell} title={detail}>{detail}</TableCell>
                  <TableCell className={styles.actionsCell}>
                    <Dialog>
                      <DialogTrigger disableButtonEnhancement>
                        <Button size="small" appearance="primary" disabled={acting}>
                          {acting ? "Working…" : "Approve"}
                        </Button>
                      </DialogTrigger>
                      <DialogSurface>
                        <DialogBody>
                          <DialogTitle>Approve request #{r.requestNumber}?</DialogTitle>
                          <DialogContent>
                            {r.requesterName} · {r.leaveTypeName} · {formatDateRange(r.startDate, r.endDate)} · {formatDays(r)}.
                            {" "}This moves {r.workDays} day(s) from pending to used on their balance and writes an audit entry.
                          </DialogContent>
                          <DialogActions>
                            <DialogTrigger disableButtonEnhancement>
                              <Button appearance="secondary">Not yet</Button>
                            </DialogTrigger>
                            <DialogTrigger disableButtonEnhancement>
                              <Button appearance="primary" onClick={() => approve(r)}>Yes, approve</Button>
                            </DialogTrigger>
                          </DialogActions>
                        </DialogBody>
                      </DialogSurface>
                    </Dialog>
                    <Button
                      size="small"
                      appearance="subtle"
                      disabled={acting}
                      onClick={() => setDenyTarget(r)}
                    >
                      Deny
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <DenyDialog
        open={denyTarget !== null}
        request={denyTarget}
        onClose={() => setDenyTarget(null)}
        onSubmit={(reason) => denyTarget ? deny(denyTarget, reason) : Promise.resolve()}
      />
    </div>
  );
}
